import React, { useState, useEffect } from 'react';
import { AlertTriangle, Loader2, Plus, FileUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import DataTable from '../../components/admin/DataTable';
import FormModal from '../../components/admin/FormModal';

export default function AdminNeighborhoods() {
  const navigate = useNavigate();
  const [neighborhoods, setNeighborhoods] = useState<any[]>([]);
  const [districts, setDistricts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingNeighborhood, setEditingNeighborhood] = useState<any>(null);
  const [formValues, setFormValues] = useState<any>({});

  // Tablo kolonları
  const columns = [
    { key: 'name', label: 'Mahalle Adı' },
    { 
      key: 'district', 
      label: 'İlçe',
      render: (item: any) => {
        if (!item?.district) return '-';
        return item.district.name;
      }
    },
    {
      key: 'city',
      label: 'İl',
      render: (item: any) => {
        if (!item?.district?.city) return '-';
        return item.district.city.name;
      }
    }
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // İlçeleri yükle
      const { data: districtsData, error: districtsError } = await supabase
        .from('districts')
        .select(`
          id,
          name,
          city:cities (
            id,
            name
          )
        `)
        .order('name');
      
      if (districtsError) throw districtsError;
      setDistricts(districtsData || []);

      // Mahalleleri yükle
      const { data: neighborhoodsData, error: neighborhoodsError } = await supabase
        .from('neighborhoods')
        .select(`
          id,
          name,
          district:districts (
            id,
            name,
            city:cities (
              id,
              name
            )
          )
        `)
        .order('name');
      
      if (neighborhoodsError) throw neighborhoodsError;
      setNeighborhoods(neighborhoodsData || []);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Veriler yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingNeighborhood(null);
    setFormValues({});
    setIsModalOpen(true);
  };

  const handleEdit = (neighborhood: any) => {
    setEditingNeighborhood(neighborhood);
    setFormValues({
      name: neighborhood.name,
      district_id: neighborhood.district?.id
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (neighborhood: any) => {
    if (!window.confirm('Bu mahalleyi silmek istediğinize emin misiniz?')) return;

    try {
      setLoading(true);
      setError(null);

      const { error } = await supabase
        .from('neighborhoods')
        .delete()
        .eq('id', neighborhood.id);

      if (error) throw error;
      
      await loadData();
    } catch (err) {
      console.error('Error deleting neighborhood:', err);
      setError('Mahalle silinirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values: any) => {
    if (!values.name || !values.district_id) {
      setError('Lütfen tüm alanları doldurun.');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      if (editingNeighborhood) {
        const { error } = await supabase
          .from('neighborhoods')
          .update({
            name: values.name,
            district_id: values.district_id
          })
          .eq('id', editingNeighborhood.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('neighborhoods')
          .insert({
            name: values.name,
            district_id: values.district_id
          });

        if (error) throw error;
      }

      await loadData();
      setIsModalOpen(false);
    } catch (err) {
      console.error('Error saving neighborhood:', err);
      setError('Mahalle kaydedilirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  // Form alanları
  const formFields = [
    { name: 'name', label: 'Mahalle Adı', type: 'text' },
    { 
      name: 'district_id', 
      label: 'İlçe', 
      type: 'select',
      options: districts.map(district => ({ 
        value: district.id, 
        label: `${district.name} (${district.city?.name || ''})`
      }))
    }
  ];

  return (
    <div className="space-y-8">
      {/* Başlık ve Butonlar */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-white">Mahalleler</h1>
        <div className="flex gap-2">
          <button
            onClick={() => navigate('/admin/neighborhoods/import')}
            className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
          >
            <FileUp className="w-4 h-4" />
            Toplu İçe Aktar
          </button>
          <button
            onClick={() => navigate('/admin/districts/multi-import')}
            className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
          >
            <FileUp className="w-4 h-4" />
            Çoklu İlçe İçe Aktar
          </button>
        </div>
      </div>

      {/* Hata Mesajı */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-red-500" />
          <p className="text-red-500">{error}</p>
        </div>
      )}

      {/* Yükleniyor veya Tablo */}
      {loading && neighborhoods.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-yellow-400 animate-spin" />
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={neighborhoods}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onAdd={handleAdd}
        />
      )}

      {/* Form Modal */}
      <FormModal
        title={editingNeighborhood ? 'Mahalle Düzenle' : 'Yeni Mahalle Ekle'}
        fields={formFields}
        values={formValues}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleSubmit}
        onChange={(name, value) => setFormValues(prev => ({ ...prev, [name]: value }))}
      />
    </div>
  );
}